import { useCallback, useEffect, useRef, useState } from 'react'
import BackRow from './BackButton'
import Icon from '../components/Icon'
import { getLocale } from '../lib/i18n'
import {
  worx,
  type Category, type ServiceCard, type ServiceDetail,
  type ServiceRequestReceived, type ServiceRequestSent, type StoryState,
} from './client'
import Report from './Report'
import { ago, categoryName, tw } from './strings'
import { rateLabel, sv, type ServicesKey } from './servicesStrings'
import { SERVICE_COLOR_IDS, SERVICE_COLORS, isServiceColor, serviceColorHex } from '../../shared/serviceColors'
import { categoryIcon } from './categories'
import { TranslatedBody, useTranslation } from './Translated'
import { formatPhone, phoneHref, whatsappHref } from '../../shared/phone'

/*
 * Services for hire: the third door.
 *
 * A provider lists what they do; a neighbour asks; the provider calls back. No
 * applications, no threads — see 0109_services.sql for why a request is a lead
 * and not a conversation. Everything here reuses the job board's classes
 * (w-card, w-form, w-sheet…) so it looks like the same app.
 */

/** The kinds of work that make sense as a service ad, in the order they are offered. */
const SERVICE_CATEGORIES = [
  'plumbing', 'electrical', 'hauling', 'handyman', 'aircon',
  'construction', 'cleaning', 'grounds', 'marine', 'transport', 'skilled', 'other',
]
const RATE_UNITS = ['hour', 'job', 'trip', 'day', 'quote'] as const


function useServiceCategories() {
  const [categories, setCategories] = useState<Category[]>([])
  useEffect(() => {
    void worx.categories()
      .then((r) => setCategories(
        SERVICE_CATEGORIES
          .map((id) => r.categories.find((c) => c.id === id))
          .filter((c): c is Category => Boolean(c)),
      ))
      .catch(() => {})
  }, [])
  return categories
}

/* ── The card ─────────────────────────────────────────────────────────────── */

function ServiceCardView({
  service, categories, onOpen, saved, onToggleSave, children,
}: {
  service: ServiceCard
  categories: Category[]
  onOpen?: () => void
  saved?: boolean
  /** Present only when somebody is signed in: a signed-out visitor has nowhere to keep one. */
  onToggleSave?: () => void
  children?: React.ReactNode
}) {
  const category = categories.find((c) => c.id === service.categoryId)
  const live = service.status === 'published'
  return (
    <article
      className={live ? 'w-card w-svc' : 'w-card w-card-ended w-svc'}
      style={{ '--svc-color': serviceColorHex(service.color) } as React.CSSProperties}
    >
      <button className="w-card-main" onClick={onOpen} disabled={!onOpen}>
        <span className="w-card-body">
          <span className="w-card-title">{service.titleTranslated ?? service.title}</span>
          <span className="w-card-employer">{service.business ?? service.ownerName}</span>
          <span className="w-card-where">
            {service.area && <><Icon name="pin" size={14} /> {service.area}</>}
            {service.area && category && <span className="w-dot" aria-hidden="true">•</span>}
            {category && categoryName(category)}
          </span>
          <span className={service.rateUnit ? 'w-pay-chip' : 'w-pay-chip muted'}>{rateLabel(service)}</span>
          {service.preferred && (
            <span className="w-pill w-pill-preferred"><Icon name="star" size={12} /> {sv('sv.pref.badge')}</span>
          )}
          {!live && (
            <span className={`w-pill w-pill-${service.status}`}>{sv(`sv.ads.status.${service.status}` as ServicesKey)}</span>
          )}
        </span>

        {/* The ad's block: its colour, its icon. A logo replaces the icon later. */}
        <span className="w-svc-art" aria-hidden="true">
          <Icon name={service.categoryId ? categoryIcon(service.categoryId) : 'wrench'} size={52} />
        </span>

        {onToggleSave && (
          <span
            className="w-bookmark"
            role="button"
            tabIndex={0}
            aria-pressed={Boolean(saved)}
            aria-label={saved ? tw('w.jobs.unsave') : tw('w.jobs.save')}
            onClick={(e) => { e.stopPropagation(); onToggleSave() }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return
              e.preventDefault(); e.stopPropagation(); onToggleSave()
            }}
          >
            <Icon name="bookmark" size={20} className={saved ? 'w-icon-filled' : undefined} />
          </span>
        )}
      </button>
      {/* Its own padded section: the card's main button carries the padding for
          the top half, so anything below it must bring its own. */}
      {children && <div className="w-card-foot">{children}</div>}
    </article>
  )
}

/* ── Find a pro ───────────────────────────────────────────────────────────── */

function deviceCanRecord(): boolean {
  return typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia)
}

/** Keeps the set of saved ids and the optimistic toggle, as the job feed does. */
function useSaved(signedIn: boolean) {
  const [saved, setSaved] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (!signedIn) return
    void worx.savedServices().then((r) => setSaved(new Set(r.services.map((s) => s.id)))).catch(() => {})
  }, [signedIn])

  async function toggle(id: string) {
    // Optimistic, and put back on failure: a bookmark that waits for a round
    // trip feels broken.
    const wanted = !saved.has(id)
    const flip = (on: boolean) => setSaved((prev) => {
      const next = new Set(prev)
      if (on) next.add(id); else next.delete(id)
      return next
    })
    flip(wanted)
    try { await worx.saveService(id, wanted) } catch { flip(!wanted) }
  }
  return { saved, toggle }
}

export function ServicesFeed({
  onOpen, onListYours, signedIn = false,
}: {
  onOpen: (id: string) => void
  onListYours?: () => void
  signedIn?: boolean
}) {
  const categories = useServiceCategories()
  const { saved, toggle } = useSaved(signedIn)
  const [services, setServices] = useState<ServiceCard[]>([])
  const [q, setQ] = useState('')
  const [category, setCategory] = useState('')
  const [next, setNext] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const load = useCallback(async (opts: { append?: boolean; before?: number } = {}) => {
    setLoading(true)
    setLoadError(false)
    try {
      const r = await worx.services({ q, category, before: opts.before })
      setServices((prev) => (opts.append ? [...prev, ...r.services] : r.services))
      setNext(r.next)
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [q, category])

  // Debounced, like the job feed: this fires on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => void load(), 250)
    return () => clearTimeout(timer)
  }, [load])

  return (
    <div className="w-feed">
      <h1 className="w-feed-heading">{sv('sv.feed.heading')}</h1>
      <p className="w-feed-sub">{sv('sv.feed.sub')}</p>

      <div className="w-search">
        <Icon name="search" size={20} />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={sv('sv.feed.search')}
          aria-label={sv('sv.feed.search')}
        />
      </div>

      {/* A select, not a chip row: twelve trades do not fit on one line, and a
          trade is something you pick from a list rather than scan for. */}
      <select
        className="w-service-filter"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        aria-label={sv('sv.feed.allCategories')}
      >
        <option value="">{sv('sv.feed.allCategories')}</option>
        {categories.map((c) => <option key={c.id} value={c.id}>{categoryName(c)}</option>)}
      </select>

      {services.map((s) => (
        <ServiceCardView
          key={s.id} service={s} categories={categories} onOpen={() => onOpen(s.id)}
          saved={saved.has(s.id)} onToggleSave={signedIn ? () => void toggle(s.id) : undefined}
        />
      ))}

      {loading && <p className="w-state" role="status">{sv('sv.feed.loading')}</p>}
      {loadError && (
        <div className="w-state w-state-error" role="alert">
          <p>{sv('sv.feed.loadError')}</p>
          <button type="button" onClick={() => void load()}>{sv('sv.feed.retry')}</button>
        </div>
      )}
      {!loading && !loadError && services.length === 0 && (
        <p className="muted w-empty">{q || category ? sv('sv.feed.none') : sv('sv.feed.noneAtAll')}</p>
      )}

      {next && (
        <button onClick={() => void load({ append: true, before: next })} disabled={loading}>
          {sv('sv.feed.more')}
        </button>
      )}

      {onListYours && (
        <button className="link" onClick={onListYours}>
          <Icon name="plus" size={16} /> {sv('sv.feed.listYours')}
        </button>
      )}
    </div>
  )
}

/* ── One ad ───────────────────────────────────────────────────────────────── */

export function ServiceDetailView({
  id, signedIn, onBack, onNeedAccount, onRequested,
}: {
  id: string
  signedIn: boolean
  onBack: () => void
  /** Signed out: take them to the account form. */
  onNeedAccount?: () => void
  onRequested?: () => void
}) {
  const categories = useServiceCategories()
  const [service, setService] = useState<ServiceDetail | null>(null)
  const tr = useTranslation(service?.translation)
  const [loadError, setLoadError] = useState(false)
  const [message, setMessage] = useState('')
  const [phone, setPhone] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Opening an ad from a scrolled feed must land on its title, not on the middle
  // of the page with the title scrolled under the status bar.
  useEffect(() => { window.scrollTo(0, 0) }, [id])

  useEffect(() => {
    setService(null)
    setLoadError(false)
    void worx.service(id).then((r) => setService(r.service)).catch(() => setLoadError(true))
  }, [id])

  async function send(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await worx.requestService(id, { message: message.trim(), phone: phone.trim() })
      setSent(true)
      onRequested?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : tw('w.error.send'))
    } finally {
      setBusy(false)
    }
  }

  if (loadError) {
    return (
      <div className="w-stack">
        <BackRow onBack={onBack} />
        <p className="error" role="alert">{sv('sv.detail.loadError')}</p>
      </div>
    )
  }
  if (!service) return <p className="w-state" role="status">{sv('sv.feed.loading')}</p>

  const category = categories.find((c) => c.id === service.categoryId)
  const live = service.status === 'published'
  const alreadyAsked = service.requested || sent

  return (
    <div className="w-detail">
      <div className="w-detail-main">
      <header className="w-detail-head">
        <BackRow onBack={onBack}>
          <h1>{tr.pick('title', service.title)}</h1>
        </BackRow>
        {tr.note}
        <p className="w-card-employer">{service.business ?? service.ownerName}</p>
        <p className="w-card-where">
          {service.area && <><Icon name="pin" size={14} /> {service.area}</>}
          {service.area && category && <span className="w-dot" aria-hidden="true">•</span>}
          {category && categoryName(category)}
        </p>
        <span className={service.rateUnit ? 'w-pay-chip' : 'w-pay-chip muted'}>{rateLabel(service)}</span>
      </header>

      {service.status === 'removed' && (
        <p className="error" role="status">
          {sv('sv.ads.removedWhy')} {service.removedReason}
        </p>
      )}
      {!live && service.status !== 'removed' && <p className="muted" role="status">{sv('sv.detail.notLive')}</p>}

      {service.story && (
        <section className="card w-story">
          <h2><Icon name="star" size={16} /> {sv('sv.pref.storyTitle')}</h2>
          <p className="w-prose">{tr.pick('story', service.story)}</p>
        </section>
      )}

      <section className="card">
        <h2>{sv('sv.detail.about')}</h2>
        <p className="w-prose">{tr.pick('description', service.description)}</p>
        {service.availability && (
          <p><strong>{sv('sv.detail.availability')}:</strong> {tr.pick('availability', service.availability)}</p>
        )}
      </section>

      {live && !service.own && !signedIn && (
        <section className="card w-browse">
          <p>{sv('sv.detail.signIn')}</p>
          {onNeedAccount && <button onClick={onNeedAccount}>{sv('sv.browse.join')}</button>}
        </section>
      )}

      </div>

      <aside className="w-detail-side">
      {live && !service.own && signedIn && (
        <>
          {service.phone && (
            <section className="card">
              <h2>{sv('sv.detail.contact')}</h2>
              <p className="w-contact-number">{formatPhone(service.phone)}</p>
              <div className="w-contact-actions">
                <a className="button" href={phoneHref(service.phone)} aria-label={`${sv('sv.detail.call')} ${formatPhone(service.phone)}`}>
                  <Icon name="phone" size={18} /> {sv('sv.detail.call')}
                </a>
                <a className="button" href={whatsappHref(service.phone)} target="_blank" rel="noopener noreferrer"
                  aria-label={`${sv('sv.detail.whatsapp')} ${formatPhone(service.phone)}`}>
                  <Icon name="chat" size={18} /> {sv('sv.detail.whatsapp')}
                </a>
              </div>
            </section>
          )}

          <section className="card">
            <h2>{sv('sv.req.title')}</h2>
            {alreadyAsked ? (
              <p className="w-ok" role="status">{sent ? sv('sv.req.sent') : sv('sv.req.already')}</p>
            ) : (
              <form className="w-form" onSubmit={send}>
                <label>
                  {sv('sv.req.message')}
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                    maxLength={1000}
                    required
                    placeholder={sv('sv.req.messageHint')}
                  />
                </label>
                <label>
                  {sv('sv.req.phone')}
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    maxLength={30}
                  />
                  <span className="muted small">{sv('sv.req.phoneHint')}</span>
                </label>
                {error && <p className="error" role="alert">{error}</p>}
                <button className="primary" type="submit" disabled={busy}>
                  <Icon name="mail" size={18} /> {busy ? '…' : sv('sv.req.send')}
                </button>
              </form>
            )}
          </section>
        </>
      )}

      {service.own && <p className="muted">{sv('sv.detail.own')}</p>}

      {signedIn && !service.own && live && (
        <Report subjectType="service" subjectId={service.id} />
      )}
      </aside>
    </div>
  )
}

/* ── Saved ────────────────────────────────────────────────────────────────── */

export function SavedServices({ onOpen }: { onOpen: (id: string) => void }) {
  const categories = useServiceCategories()
  const [services, setServices] = useState<ServiceCard[] | null>(null)
  const { saved, toggle } = useSaved(true)

  useEffect(() => {
    void worx.savedServices().then((r) => setServices(r.services)).catch(() => setServices([]))
  }, [])

  if (services === null) return <p className="w-state" role="status">{sv('sv.feed.loading')}</p>

  return (
    <div className="w-feed">
      <h1 className="w-feed-heading">{sv('sv.tab.saved')}</h1>
      {services.length === 0 && (
        <div className="w-empty">
          <p>{sv('sv.saved.none')}</p>
          <p className="muted small">{sv('sv.saved.noneHint')}</p>
        </div>
      )}
      {services.map((s) => (
        <ServiceCardView
          key={s.id} service={s} categories={categories}
          // A paused ad stays listed but cannot be opened for contact.
          onOpen={s.status === 'published' ? () => onOpen(s.id) : undefined}
          // Unsaving here leaves the card in place until the next visit, so a
          // mis-tap can be undone by tapping again.
          saved={saved.has(s.id)}
          onToggleSave={() => void toggle(s.id)}
        />
      ))}
    </div>
  )
}

/* ── My requests (the ones I sent) ────────────────────────────────────────── */

export function MyRequests({ onOpen }: { onOpen: (serviceId: string) => void }) {
  const [requests, setRequests] = useState<ServiceRequestSent[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setRequests((await worx.mySentRequests()).requests)
    } catch {
      setRequests([])
      setError(tw('w.error.work'))
    }
  }, [])
  useEffect(() => { void load() }, [load])

  async function cancel(id: string) {
    setError(null)
    try {
      await worx.setServiceRequestStatus(id, 'cancelled')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : tw('w.error.work'))
    }
  }

  if (requests === null) return <p className="w-state" role="status">{sv('sv.feed.loading')}</p>

  return (
    <div className="w-feed">
      <h1 className="w-feed-heading">{sv('sv.tab.requests')}</h1>
      {error && <p className="error" role="alert">{error}</p>}
      {requests.length === 0 && (
        <div className="w-empty">
          <p>{sv('sv.req.none')}</p>
          <p className="muted small">{sv('sv.req.noneHint')}</p>
        </div>
      )}
      {requests.map((r) => (
        <article key={r.id} className="w-card">
          <div className="w-card-main-static">
            <span className="w-card-body">
              <span className="w-card-title">{r.serviceTitle}</span>
              {r.ownerName && <span className="w-card-employer">{r.ownerName}</span>}
              <span className="w-prose">{r.message}</span>
              <span className={`w-pill w-pill-${r.status}`}>{sv(`sv.req.status.${r.status}` as ServicesKey)}</span>
              <span className="w-posted">{ago(r.createdAt)}</span>
            </span>
          </div>
          <div className="w-card-foot">
          <div className="w-actions">
            {r.serviceStatus === 'published'
              ? <button onClick={() => onOpen(r.serviceId)}>{sv('sv.detail.contact')}</button>
              : <span className="muted small">{sv('sv.req.adGone')}</span>}
            {r.status === 'new' && <button className="link" onClick={() => void cancel(r.id)}>{sv('sv.req.cancel')}</button>}
          </div>
          </div>
        </article>
      ))}
    </div>
  )
}

/* ── Preferred: tell your story ───────────────────────────────────────────── */

/**
 * The interview. Renders nothing when preferred is off for this ad (the server
 * answers 404), so an owner who has not been offered it never sees it.
 */
function PreferredPanel({ serviceId }: { serviceId: string }) {
  const [state, setState] = useState<StoryState | null>(null)
  const [open, setOpen] = useState(false)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [draft, setDraft] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Recording. `mode` is 'record' unless this device or server cannot, and the
  // owner can always flip to typing: a denied microphone must not strand them.
  const [mode, setMode] = useState<'record' | 'type'>('record')
  const [recording, setRecording] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [clip, setClip] = useState<Blob | null>(null)
  const [transcript, setTranscript] = useState<string | null>(null)
  const recorder = useRef<MediaRecorder | null>(null)
  const stream = useRef<MediaStream | null>(null)
  const chunks = useRef<Blob[]>([])
  const timer = useRef<ReturnType<typeof setInterval> | null>(null)
  const MAX_SECONDS = 150

  // Fixed for the life of the page, so a module-level read rather than a value
  // every hook has to list as a dependency.
  const canRecord = deviceCanRecord()

  const stopTracks = useCallback(() => {
    if (timer.current) { clearInterval(timer.current); timer.current = null }
    stream.current?.getTracks().forEach((t) => t.stop())
    stream.current = null
  }, [])
  // Leaving the screen mid-recording must let go of the microphone.
  useEffect(() => () => {
    if (recorder.current?.state === 'recording') recorder.current.stop()
    stopTracks()
  }, [stopTracks])

  async function startRecording() {
    setError(null); setClip(null)
    try {
      const media = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.current = media
      // The first type this browser can write: webm/opus where it can, mp4 on Safari.
      const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((t) => MediaRecorder.isTypeSupported(t))
      const rec = new MediaRecorder(media, mime ? { mimeType: mime } : undefined)
      chunks.current = []
      rec.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data) }
      rec.onstop = () => {
        stopTracks()
        setRecording(false)
        setClip(new Blob(chunks.current, { type: rec.mimeType || mime || 'audio/webm' }))
      }
      rec.start()
      recorder.current = rec
      setSeconds(0); setRecording(true)
      timer.current = setInterval(() => {
        setSeconds((n) => {
          if (n + 1 >= MAX_SECONDS && recorder.current?.state === 'recording') recorder.current.stop()
          return n + 1
        })
      }, 1000)
    } catch {
      stopTracks()
      setError(sv('sv.pref.micDenied'))
      setMode('type')
    }
  }
  function stopRecording() { if (recorder.current?.state === 'recording') recorder.current.stop() }

  const load = useCallback(async () => {
    try {
      const r = await worx.story(serviceId)
      setState(r)
      setAnswers((a) => (Object.keys(a).length ? a : r.answers))
      setDraft(r.draft)
      // Recording needs the browser, the writing key and speech-to-text.
      setMode(r.voiceReady && deviceCanRecord() ? 'record' : 'type')
    } catch {
      setState(null)
    }
  }, [serviceId])
  useEffect(() => { void load() }, [load])

  if (!state) return null

  async function write() {
    setBusy(true); setError(null); setMessage(null)
    try {
      if (mode === 'record' && clip) {
        const r = await worx.voiceStory(serviceId, clip, getLocale())
        setDraft(r.draft); setTranscript(r.transcript)
        setState((st) => (st ? { ...st, balance: r.balance } : st))
        return
      }
      const r = await worx.draftStory(serviceId, answers, getLocale())
      setDraft(r.draft)
      setState((st) => (st ? { ...st, balance: r.balance } : st))
    } catch (err) {
      // The server's own sentence is accurate and already plain; fall back to
      // ours only when there is none.
      setError(err instanceof Error ? err.message : sv('sv.pref.error'))
      await load()
    } finally {
      setBusy(false)
    }
  }

  async function approve() {
    setBusy(true); setError(null)
    try {
      await worx.approveStory(serviceId, draft ?? undefined)
      setOpen(false); setDraft(null); setClip(null); setTranscript(null)
      setMessage(sv('sv.pref.approved'))
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : sv('sv.pref.error'))
    } finally {
      setBusy(false)
    }
  }

  async function withdraw() {
    setBusy(true); setError(null)
    try { await worx.withdrawStory(serviceId); setMessage(null); await load() }
    catch (err) { setError(err instanceof Error ? err.message : sv('sv.pref.error')) }
    finally { setBusy(false) }
  }

  return (
    <div className="w-preferred">
      <h3><Icon name="star" size={16} /> {sv('sv.pref.heading')}</h3>

      {state.story && !open && (
        <>
          <p className="w-prose w-story-text">{state.story}</p>
          <div className="w-actions">
            <button onClick={() => setOpen(true)}>{sv('sv.pref.edit')}</button>
            <button className="link" disabled={busy} onClick={() => void withdraw()}>{sv('sv.pref.withdraw')}</button>
          </div>
        </>
      )}

      {!state.story && !open && (
        <>
          <p className="muted small">{sv('sv.pref.intro')}</p>
          <button onClick={() => setOpen(true)}>{sv('sv.pref.start')}</button>
        </>
      )}

      {open && !draft && mode === 'record' && (
        <div className="w-form">
          <p className="muted small">{sv('sv.pref.talk')}</p>
          <ul className="w-talk">
            {state.questions.map((q) => <li key={q}>{sv(`sv.pref.q.${q}` as ServicesKey)}</li>)}
          </ul>

          {!clip && (
            <button
              type="button"
              className={recording ? 'w-record w-record-on' : 'w-record'}
              aria-pressed={recording}
              onClick={recording ? stopRecording : () => void startRecording()}
              disabled={busy}
            >
              <Icon name={recording ? 'pause' : 'sound'} size={28} />
              <span>{recording ? `${sv('sv.pref.stop')} · ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` : sv('sv.pref.record')}</span>
            </button>
          )}
          {recording && <p className="muted small" role="status">{sv('sv.pref.recording')}</p>}

          {clip && (
            <>
              <p className="w-ok" role="status">{sv('sv.pref.recorded')} · {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}</p>
              <button type="button" onClick={() => { setClip(null); void startRecording() }} disabled={busy}>{sv('sv.pref.again')}</button>
            </>
          )}

          <p className="muted small">
            {sv('sv.pref.cost')} {state.cost} · {sv('sv.pref.balance')}: {state.balance}
          </p>
          {error && <p className="error" role="alert">{error}</p>}
          <button className="primary" disabled={busy || !clip || state.balance < state.cost} onClick={() => void write()}>
            {busy ? sv('sv.pref.writing') : sv('sv.pref.write')}
          </button>
          {state.balance < state.cost && <p className="error small">{sv('sv.pref.outOfTokens')}</p>}
          <div className="w-actions">
            <button className="link" onClick={() => { stopRecording(); setMode('type') }}>{sv('sv.pref.typeInstead')}</button>
            <button className="link" onClick={() => { stopRecording(); setClip(null); setOpen(false) }}>{sv('sv.pref.cancel')}</button>
          </div>
        </div>
      )}

      {open && !draft && mode === 'type' && (
        <div className="w-form">
          <p className="muted small">{sv('sv.pref.hint')}</p>
          {state.questions.map((q) => (
            <label key={q}>
              {sv(`sv.pref.q.${q}` as ServicesKey)}
              <textarea
                rows={3}
                maxLength={1000}
                value={answers[q] ?? ''}
                onChange={(e) => setAnswers((a) => ({ ...a, [q]: e.target.value }))}
              />
            </label>
          ))}
          <p className="muted small">
            {sv('sv.pref.cost')} {state.cost} · {sv('sv.pref.balance')}: {state.balance}
          </p>
          {!state.aiReady && <p className="muted small">{sv('sv.pref.notReady')}</p>}
          {error && <p className="error" role="alert">{error}</p>}
          <button className="primary" disabled={busy || !state.aiReady || state.balance < state.cost} onClick={() => void write()}>
            {busy ? sv('sv.pref.writing') : sv('sv.pref.write')}
          </button>
          {state.balance < state.cost && <p className="error small">{sv('sv.pref.outOfTokens')}</p>}
          <div className="w-actions">
            {state.voiceReady && canRecord && (
              <button className="link" onClick={() => setMode('record')}>{sv('sv.pref.recordInstead')}</button>
            )}
            <button className="link" onClick={() => { setOpen(false) }}>{sv('sv.pref.cancel')}</button>
          </div>
        </div>
      )}

      {open && draft && (
        <div className="w-form">
          {transcript && (
            <details>
              <summary>{sv('sv.pref.heard')}</summary>
              <p className="w-prose muted small">{transcript}</p>
            </details>
          )}
          <h4>{sv('sv.pref.draftTitle')}</h4>
          <p className="muted small">{sv('sv.pref.draftHint')}</p>
          <textarea rows={8} maxLength={1200} value={draft} onChange={(e) => setDraft(e.target.value)} aria-label={sv('sv.pref.draftTitle')} />
          {error && <p className="error" role="alert">{error}</p>}
          <button className="primary" disabled={busy} onClick={() => void approve()}>{sv('sv.pref.approve')}</button>
          <div className="w-actions">
            <button disabled={busy || state.balance < state.cost} onClick={() => void write()}>
              {sv('sv.pref.retry')} ({state.cost})
            </button>
            <button className="link" onClick={() => { setDraft(null); setClip(null); setTranscript(null); setOpen(false) }}>{sv('sv.pref.cancel')}</button>
          </div>
        </div>
      )}

      {message && <p className="w-ok" role="status">{message}</p>}
    </div>
  )
}

/* ── My services (the ones I offer) ───────────────────────────────────────── */

type Mine = Awaited<ReturnType<typeof worx.myServices>>['services'][number]

interface Form {
  title: string; business: string; categoryId: string; description: string; area: string
  availability: string; rateMin: string; rateMax: string; rateUnit: string; phone: string; color: string
}
const BLANK: Form = {
  title: '', business: '', categoryId: '', description: '', area: '',
  availability: '', rateMin: '', rateMax: '', rateUnit: 'job', phone: '', color: '',
}

function ServiceForm({
  service, categories, onDone,
}: {
  service: Mine | null
  categories: Category[]
  onDone: () => void
}) {
  const [form, setForm] = useState<Form>(() => service ? {
    title: service.title,
    business: service.business ?? '',
    categoryId: service.categoryId ?? '',
    description: service.description,
    area: service.area ?? '',
    availability: service.availability ?? '',
    rateMin: service.rateMin?.toString() ?? '',
    rateMax: service.rateMax?.toString() ?? '',
    rateUnit: service.rateUnit ?? 'job',
    phone: service.phone ?? '',
    color: isServiceColor(service.color) ? service.color : '',
  } : BLANK)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    const body = {
      title: form.title,
      business: form.business,
      categoryId: form.categoryId || null,
      description: form.description,
      area: form.area,
      availability: form.availability,
      rateUnit: form.rateUnit,
      rateMin: form.rateMin === '' ? null : Number(form.rateMin),
      rateMax: form.rateMax === '' ? null : Number(form.rateMax),
      phone: form.phone,
      color: form.color || null,
    }
    try {
      if (service) await worx.updateService(service.id, body)
      else await worx.createService(body)
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : sv('sv.form.error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="w-form" onSubmit={submit}>
      <BackRow onBack={onDone}>
        <h2>{service ? service.title : sv('sv.form.new')}</h2>
      </BackRow>

      <label>
        <span className="w-label">{sv('sv.form.title')} <span className="w-required">*</span></span>
        <input value={form.title} onChange={set('title')} required maxLength={100} placeholder={sv('sv.form.titleExample')} />
      </label>
      <label>{sv('sv.form.business')}<input value={form.business} onChange={set('business')} maxLength={100} /></label>
      <label>
        {sv('sv.form.category')}
        <select value={form.categoryId} onChange={set('categoryId')}>
          <option value="">—</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{categoryName(c)}</option>)}
        </select>
      </label>
      <label>
        <span className="w-label">{sv('sv.form.description')} <span className="w-required">*</span></span>
        <textarea value={form.description} onChange={set('description')} rows={5} maxLength={4000} required />
      </label>
      <label>
        {sv('sv.form.area')}
        <input value={form.area} onChange={set('area')} maxLength={120} placeholder={sv('sv.form.areaExample')} />
      </label>
      <label>{sv('sv.form.availability')}<input value={form.availability} onChange={set('availability')} maxLength={200} /></label>

      <fieldset>
        <legend>{sv('sv.form.rate')}</legend>
        <div className="w-pay-row">
          <label className="w-pay-field">
            <span className="muted small">{sv('sv.form.rateFrom')}</span>
            <input type="number" inputMode="numeric" min={0} value={form.rateMin} onChange={set('rateMin')} disabled={form.rateUnit === 'quote'} />
          </label>
          <label className="w-pay-field">
            <span className="muted small">{sv('sv.form.rateTo')}</span>
            <input type="number" inputMode="numeric" min={0} value={form.rateMax} onChange={set('rateMax')} disabled={form.rateUnit === 'quote'} />
          </label>
          <label className="w-pay-field w-pay-unit">
            <span className="muted small">{sv('sv.form.rateUnit')}</span>
            <select value={form.rateUnit} onChange={set('rateUnit')}>
              {RATE_UNITS.map((u) => <option key={u} value={u}>{sv(`sv.rate.${u}` as ServicesKey)}</option>)}
            </select>
          </label>
        </div>
      </fieldset>

      <label>
        <span className="w-label">{sv('sv.form.phone')} <span className="w-required">*</span></span>
        <input type="tel" inputMode="tel" value={form.phone} onChange={set('phone')} maxLength={30} />
        <span className="muted small">{sv('sv.form.phoneHint')}</span>
      </label>

      <fieldset className="w-colors">
        <legend>{sv('sv.form.color')}</legend>
        <div className="w-swatches" role="radiogroup" aria-label={sv('sv.form.color')}>
          {SERVICE_COLOR_IDS.map((id) => (
            <button
              key={id} type="button" role="radio" aria-checked={(form.color || 'teal') === id}
              aria-label={sv(`sv.color.${id}` as ServicesKey)} className="w-swatch"
              style={{ background: SERVICE_COLORS[id] }}
              onClick={() => setForm((f) => ({ ...f, color: id }))}
            >
              {(form.color || 'teal') === id && <Icon name="check" size={20} />}
            </button>
          ))}
        </div>
        <span className="muted small">{sv('sv.form.colorHint')}</span>
      </fieldset>

      {error && <p className="error" role="alert">{error}</p>}
      <button className="primary" type="submit" disabled={busy}>{busy ? '…' : sv('sv.form.save')}</button>
    </form>
  )
}

function Received({ serviceId }: { serviceId: string }) {
  const [requests, setRequests] = useState<ServiceRequestReceived[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try { setRequests((await worx.serviceRequests(serviceId)).requests) } catch { setRequests([]) }
  }, [serviceId])
  useEffect(() => { void load() }, [load])

  async function mark(id: string, status: 'new' | 'handled') {
    setError(null)
    try {
      await worx.setServiceRequestStatus(id, status)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : tw('w.error.work'))
    }
  }

  if (requests === null) return <p className="w-state" role="status">{sv('sv.feed.loading')}</p>
  if (requests.length === 0) return <p className="muted small">{sv('sv.ads.noRequests')}</p>

  return (
    <div className="w-stack">
      {error && <p className="error" role="alert">{error}</p>}
      {requests.map((r) => (
        <div key={r.id} className="card">
          <p><strong>{r.requesterName}</strong> <span className="muted small">{ago(r.createdAt)}</span></p>
          <TranslatedBody className="w-prose" text={r.message} translated={r.translation?.text.message} from={r.translation?.from} />
          <div className="w-actions w-actions-wrap">
            <a className="button" href={phoneHref(r.phone)} aria-label={`${sv('sv.detail.call')} ${formatPhone(r.phone)}`}>
              <Icon name="phone" size={18} /> {formatPhone(r.phone)}
            </a>
            <a className="button" href={whatsappHref(r.phone)} target="_blank" rel="noopener noreferrer"
              aria-label={`${sv('sv.detail.whatsapp')} ${formatPhone(r.phone)}`}>
              <Icon name="chat" size={18} /> {sv('sv.detail.whatsapp')}
            </a>
            {r.status === 'new'
              ? <button onClick={() => void mark(r.id, 'handled')}>{sv('sv.ads.markHandled')}</button>
              : <button className="link" onClick={() => void mark(r.id, 'new')}>{sv('sv.ads.markNew')}</button>}
          </div>
        </div>
      ))}
    </div>
  )
}

export function MyServices({ startNew, onStartedNew, onChanged }: {
  startNew?: boolean
  onStartedNew?: () => void
  /** The new-request count may have changed; the tab badge re-reads it. */
  onChanged?: () => void
}) {
  const categories = useServiceCategories()
  const [services, setServices] = useState<Mine[] | null>(null)
  const [editing, setEditing] = useState<Mine | 'new' | null>(null)
  const [openRequests, setOpenRequests] = useState<string | null>(null)
  // Two taps to delete, in the page rather than a native dialog: window.confirm
  // is unreliable inside the iOS shell, and a deletion takes its requests too.
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try { setServices((await worx.myServices()).services) } catch { setServices([]); setError(tw('w.error.work')) }
    onChanged?.()
  }, [onChanged])
  useEffect(() => { void load() }, [load])

  useEffect(() => {
    if (startNew) { setEditing('new'); onStartedNew?.() }
  }, [startNew, onStartedNew])

  async function run(fn: () => Promise<unknown>) {
    setError(null)
    try { await fn(); await load() } catch (err) {
      setError(err instanceof Error ? err.message : tw('w.error.work'))
    }
  }

  if (editing) {
    return (
      <ServiceForm
        service={editing === 'new' ? null : editing}
        categories={categories}
        onDone={() => { setEditing(null); void load() }}
      />
    )
  }
  if (services === null) return <p className="w-state" role="status">{sv('sv.feed.loading')}</p>

  return (
    <div className="w-feed">
      <h1 className="w-feed-heading">{sv('sv.ads.heading')}</h1>
      <p className="w-feed-sub">{sv('sv.ads.sub')}</p>
      <button className="primary" onClick={() => setEditing('new')}>
        <Icon name="plus" size={18} /> {sv('sv.ads.new')}
      </button>
      {error && <p className="error" role="alert">{error}</p>}

      {services.length === 0 && (
        <div className="w-empty">
          <p>{sv('sv.ads.none')}</p>
          <p className="muted small">{sv('sv.ads.noneHint')}</p>
        </div>
      )}

      {services.map((s) => (
        <ServiceCardView key={s.id} service={s} categories={categories}>
          {s.status === 'removed' && s.removedReason && (
            <p className="error small">{sv('sv.ads.removedWhy')} {s.removedReason}</p>
          )}
          <div className="w-actions">
            {s.status !== 'removed' && <button onClick={() => setEditing(s)}>{sv('sv.ads.edit')}</button>}
            {(s.status === 'draft' || s.status === 'paused') && (
              <button className="primary" onClick={() => void run(() => worx.setServiceStatus(s.id, 'published'))}>
                {sv('sv.ads.publish')}
              </button>
            )}
            {s.status === 'published' && (
              <button onClick={() => void run(() => worx.setServiceStatus(s.id, 'paused'))}>{sv('sv.ads.pause')}</button>
            )}
            {confirmDelete !== s.id && (
              <button className="link" onClick={() => setConfirmDelete(s.id)}>{sv('sv.ads.delete')}</button>
            )}
          </div>
          {confirmDelete === s.id && (
            <div className="w-confirm" role="alert">
              <p className="small">{sv('sv.ads.deleteConfirm')}</p>
              <div className="w-actions">
                <button onClick={() => { setConfirmDelete(null); void run(() => worx.deleteService(s.id)) }}>
                  {sv('sv.ads.deleteYes')}
                </button>
                <button className="link" onClick={() => setConfirmDelete(null)}>{sv('sv.ads.deleteNo')}</button>
              </div>
            </div>
          )}
          <PreferredPanel serviceId={s.id} />
          <button
            className="w-requests-toggle"
            aria-expanded={openRequests === s.id}
            onClick={() => setOpenRequests(openRequests === s.id ? null : s.id)}
          >
            {sv('sv.ads.requests')} ({s.requests ?? 0})
            {Boolean(s.newRequests) && <span className="w-new"> · {s.newRequests} {sv('sv.ads.newCount')}</span>}
          </button>
          {openRequests === s.id && <Received serviceId={s.id} />}
        </ServiceCardView>
      ))}
    </div>
  )
}
